import { motion, useAnimationControls, useDragControls } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router';
import { Headline, Input, Spinner } from '../components';
import { useCustomers, useRecentlyUsed } from '../hooks';
import snippets from '../i18n/lang';
import { classNames } from '../utils';
import { getFallbackRange } from '../utils/getFallbackRange';

export const AvailableCustomers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragConstraints = useRef(null);
  const { onCustomerSelect } = useRecentlyUsed();
  const dragControls = useDragControls();
  const animationControls = useAnimationControls();
  const [height, setHeight] = useState(0);

  const { data: customers, isLoading } = useCustomers();

  useEffect(() => {
    if (!customers) return;
    if (customers.length === 1) window.location.href = `/${customers[0].dataManagerID}`;

    const changeHeight = () => {
      setHeight(() => {
        const screenHeight = window.innerHeight;
        const aboveContent = document.querySelector('#top')?.clientHeight || 0;
        return screenHeight - aboveContent - 150;
      });
    };

    changeHeight();

    window.addEventListener('resize', () => changeHeight());

    return () => {
      window.removeEventListener('resize', () => changeHeight());
    };
  }, [customers]);

  const filteredCustomers = customers?.filter((customer: any) =>
    customer.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div>
      <Headline className="mb-4">{snippets.customerOverview.available}</Headline>
      <Input
        name="search"
        placeholder={snippets.customerOverview.searchLabel}
        onChange={(e) => {
          animationControls.start({ x: 0, y: 0 });

          setSearchTerm(e.target.value);
        }}
      />
      <div className="mb-3"></div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          style={{
            height: height,
          }}
          className="overflow-hidden  "
          ref={dragConstraints}
        >
          <motion.div
            drag="y"
            animate={animationControls}
            dragControls={dragControls}
            dragConstraints={dragConstraints}
            className="flex mt-3 flex-col gap-2"
            onDragStart={(e) => setIsDragging(true)}
            onDragEnd={(e) => setIsDragging(false)}
          >
            {customers
              ? filteredCustomers.length
                ? filteredCustomers
                    ?.sort((a, b) => {
                      return a.title.localeCompare(b.title) === 1 ? 1 : -1;
                    })
                    .map((customer: any) => (
                      <Link
                        to={`/${customer.dataManagerID}?start=${getFallbackRange().start}&end=${
                          getFallbackRange().end
                        }`}
                        draggable={false}
                        className={classNames(
                          'border p-3 flex rounded-lg hover:bg-gray-100 cursor-pointer gap-5 items-center',
                          isDragging ? 'pointer-events-none' : 'pointer-events-auto',
                        )}
                        key={customer.shortID}
                        onClick={() => !isDragging && onCustomerSelect(customer)}
                      >
                        <div className="h-6 w-2 rounded-lg" style={{ backgroundColor: customer.hexColor }} />
                        <div className="flex gap-5 items-center">
                          <div className="aspect-square flex items-center justify-center rounded-full h-8 overflow-hidden">
                            {customer.favicon ? (
                              <img draggable={false} src={customer.favicon} className="h-full w-full bg-gray-50 " />
                            ) : (
                              <div
                                className="h-full w-full bg-gray-50 flex items-center justify-center"
                                style={{ color: customer.hexColor }}
                              >
                                {customer.title.substr(0, 2).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <span className="text-gray-600 text-lg font-normal">{customer.title}</span>
                        </div>
                      </Link>
                    ))
                : 'Nichts gefunden'
              : Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      draggable={false}
                      className={classNames(
                        'border p-4 flex rounded-lg hover:bg-gray-100 cursor-pointer gap-3 items-center',
                      )}
                    >
                      <div className="h-6 w-2 bg-gray-100 rounded-lg" />
                      <div className="flex gap-3 items-center">
                        <Skeleton
                          circle
                          className="aspect-square flex items-center justify-center rounded-full h-8 overflow-hidden"
                        />
                        <span className="text-gray-600 w-full text-lg font-normal">
                          <Skeleton width={'100px'} />
                        </span>
                      </div>
                    </div>
                  ))}
          </motion.div>
        </div>
      )}

      {customers?.length === 0 && <small className="text-gray-300 text-xs">{snippets.customerOverview.notice}</small>}
    </div>
  );
};
